import { useMemo, useState } from "react";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
import FwUpgradePresenter from "./FwUpgradePresenter";
import {
  COVA_MODEL,
  FIRMWARE_FEATURE_KEY,
  FirmwareUpgradeDto,
  FirmwareUpgradeDtoResponse,
} from "api/interfaces/managerInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  getFirmwareUpgrade,
  postCreateFirmwareUpgrade,
  postFirmwareVersionInfo,
  putUpdateFirmwareUpgrade,
} from "api/manageAPI";
import { useQuery } from "react-query";
const initialData: FirmwareUpgradeDto = {
  isEnabled: false,
  updateTime: "04:00",
  notifyPush: true,
  notifyEmail: true,
};

export default function FwUpgradeContainer() {
  const [data, setData] = useState<FirmwareUpgradeDto>({
    ...initialData,
  });
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { notifySuccess, handleErrorNotify, createCustomError, lang } =
    useApiError();
  const firmwareVersionInfo16CH = useQuery(
    ["firmwareUpgradeVersionInfo16CH", selectedAccount.accountId],
    () =>
      postFirmwareVersionInfo({
        featureKey: FIRMWARE_FEATURE_KEY.FIRMWARE_UPGRADE,
        displayType: "COVA",
        model: COVA_MODEL.DW_COVA16,
      }),
    {
      enabled: !!selectedAccount.accountId,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );
  const firmwareVersionInfo4CH = useQuery(
    ["firmwareUpgradeVersionInfo4CH", selectedAccount.accountId],
    () =>
      postFirmwareVersionInfo({
        featureKey: FIRMWARE_FEATURE_KEY.FIRMWARE_UPGRADE,
        displayType: "COVA",
        model: COVA_MODEL.DW_COVA4,
      }),
    {
      enabled: !!selectedAccount.accountId,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );
  const firmwareAvailable = useMemo(() => {
    return {
      CH4: firmwareVersionInfo4CH?.data?.result?.minVersion,
      CH16: firmwareVersionInfo16CH.data?.result?.minVersion,
    };
  }, [
    firmwareVersionInfo4CH?.data?.result?.minVersion,
    firmwareVersionInfo16CH.data?.result?.minVersion,
  ]);
  const {
    data: firmwareUpgradeData,
    refetch,
    isLoading,
    isFetching,
    isSuccess,
  } = useQuery(
    ["firmwareUpgradeDefaultData", selectedAccount.accountId],
    () => getFirmwareUpgrade(selectedAccount.accountId),
    {
      onSuccess: (res: FirmwareUpgradeDtoResponse) => {
        if (res.error !== 0 || !res.result) {
          handleErrorNotify(
            createCustomError(res, lang.label.setting.firmware.update.fail),
            lang.label.setting.firmware.update.fail
          );
          return;
        }
        if (res.result.updateTime) {
          setData(res.result);
        } else {
          setData({ ...initialData, ...res.result });
        }
      },
      onError: (err: any) => {
        handleErrorNotify(err, lang.label.setting.firmware.update.fail);
        setData({ ...initialData });
      },
      retry: 0,
      enabled: !!selectedAccount.accountId,
      refetchOnWindowFocus: false,
    }
  );
  const onSuccess = (res: FirmwareUpgradeDtoResponse) => {
    if (res.error !== 0 || !res.result) {
      handleErrorNotify(
        createCustomError(res, lang.label.setting.firmware.update.fail),
        lang.label.setting.firmware.update.fail
      );
      return;
    }
    notifySuccess(lang.label.setting.firmware.update.success);
    refetch();
  };

  const mutationCreateFirmwareUpgrade = useStatusMutation(
    postCreateFirmwareUpgrade,
    {
      onSuccess,
      onError: (err: any) => {
        handleErrorNotify(err, lang.label.setting.firmware.update.fail);
      },
    }
  );
  const mutationUpdateFirmwareUpgrade = useStatusMutation(
    putUpdateFirmwareUpgrade,
    {
      onSuccess,
      onError: (err: any) => {
        handleErrorNotify(err, lang.label.setting.firmware.update.fail);
      },
    }
  );

  const onChange = (key: keyof FirmwareUpgradeDto, value: boolean | string) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const isChange = useMemo(() => {
    let prevData = initialData;
    if (isSuccess) {
      if (firmwareUpgradeData?.result?.updateTime) {
        prevData = firmwareUpgradeData.result;
      } else {
        prevData = { ...initialData, ...firmwareUpgradeData.result };
      }
    }
    return Object.keys(data).some(
      (key) =>
        data[key as keyof FirmwareUpgradeDto] !==
        prevData[key as keyof FirmwareUpgradeDto]
    );
  }, [data, firmwareUpgradeData?.result, isSuccess]);

  const onClickSave = () => {
    if (!mutationCreateFirmwareUpgrade.idle) return;
    if (!mutationUpdateFirmwareUpgrade.idle) return;
    const isEdit = Boolean(firmwareUpgradeData?.result?.updateTime);
    if (isEdit) {
      mutationUpdateFirmwareUpgrade.mutate({
        accountId: selectedAccount.accountId,
        payload: data,
      });
    } else {
      mutationCreateFirmwareUpgrade.mutate({
        accountId: selectedAccount.accountId,
        payload: data,
      });
    }
  };
  return (
    <FwUpgradePresenter
      data={data}
      onChange={onChange}
      onClickSave={onClickSave}
      isLoading={isLoading || isFetching}
      disabled={
        !isChange ||
        !mutationCreateFirmwareUpgrade.idle ||
        !mutationUpdateFirmwareUpgrade.idle
      }
      firmwareAvailable={firmwareAvailable}
    />
  );
}
